import { formatShort } from './deadline';
import type { PostingListItem } from './types';

/**
 * The dates a bidder has to clear, in the order they arrive.
 *
 * מפגש מציעים → שאלות הבהרה → הגשה. A tender is not one deadline but a SEQUENCE, and the submission
 * date — the only one the card used to show — is the last of them. Someone who sees "נותרו 12 ימים"
 * and nothing else can easily have already missed the mandatory bidders' meeting, which is the gate
 * that actually disqualifies them.
 *
 * Rendered only with TWO OR MORE dates. A strip with a single entry restates the countdown block
 * beside it and adds a row of chrome to every card for nothing.
 *
 * Past gates are struck through rather than dropped: removing them would make the strip change
 * length as time passes and lose the reader's place, and "the meeting has already happened" is the
 * single most decision-relevant thing the card can say.
 */

interface Gate {
  name: string;
  iso: string;
}

/** The gates this posting actually has, chronological. */
function gatesOf(item: PostingListItem): Gate[] {
  const gates: Gate[] = [];
  // The list projects `meetings.date` only; the first meeting is the one a card has room for.
  const meeting = item.meetings.find((m) => m.date !== null)?.date;
  if (meeting) gates.push({ name: 'מפגש מציעים', iso: meeting });
  if (item.dates.clarification) gates.push({ name: 'שאלות הבהרה', iso: item.dates.clarification });
  if (item.dates.lastSubmissionDate) gates.push({ name: 'הגשה', iso: item.dates.lastSubmissionDate });
  return gates;
}

export function GateStrip({ item, now = Date.now() }: { item: PostingListItem; now?: number }) {
  const gates = gatesOf(item);
  if (gates.length < 2) return null;

  return (
    <ul className="tenders-gates">
      {gates.map((gate) => {
        const at = Date.parse(gate.iso);
        // An unparseable date is treated as still ahead rather than silently struck through — NaN
        // comparisons are false in both directions, so this has to be stated.
        const past = !Number.isNaN(at) && at < now;

        return (
          <li key={gate.name} className={past ? 'tenders-gate tenders-gate--done' : 'tenders-gate tenders-gate--live'}>
            <span className="tenders-gate__dot" aria-hidden="true" />
            {/* DATE FIRST, name after it. In RTL the first element is the rightmost, so the reader
                meets the date at the start edge and the label only qualifies it — which is the way
                round a strip of dates is scanned: down the column of numbers, not the words. */}
            <span className="tenders-gate__date">{formatShort(gate.iso)}</span>
            <span className="tenders-gate__name">{gate.name}</span>
            {/* Spoken, not drawn: the strike-through carries this visually and says nothing to a
                screen reader. */}
            {past && <span className="huaca-visually-hidden">(חלף)</span>}
          </li>
        );
      })}
    </ul>
  );
}
